'use strict';

const { escapeHtml } = require('../view-utils');

const BOSS_FIELDS = Object.freeze([
  ['health', 'Boss 血量', 1, 10000, 1],
  ['timeLimitSeconds', '限时（秒）', 10, 300, 1],
  ['playerShotInterval', '玩家射击间隔（秒）', 0.05, 2, 0.01],
  ['playerShotSpeed', '玩家子弹速度', 100, 2000, 1],
  ['carrotSpeed', '红萝卜速度', 20, 1200, 1],
  ['fireInterval', 'Boss 发射间隔（秒）', 0.08, 10, 0.01],
  ['volleyCount', '单次发射数量', 1, 30, 1],
  ['maxCarrots', '同屏红萝卜上限', 1, 500, 1],
  ['moveSpeed', 'Boss 移动速度', 0, 800, 1]
]);
const BOSS_PATTERNS = Object.freeze([
  ['aimedCarrot', '瞄准'], ['fanCarrot', '扇形'], ['carrotRain', '萝卜雨'],
  ['spiralCarrot', '螺旋'], ['crossCarrot', '交叉'], ['ringBurst', '环形'],
  ['homingCarrot', '追踪'], ['sweepWall', '扫射墙'], ['splitCarrot', '分裂'],
  ['mixedCarrot', '混合']
]);
const PATTERN_LABELS = Object.fromEntries(BOSS_PATTERNS);
const SOURCE_LABELS = { builtIn: '默认曲线', global: '全局覆盖', level: '单关覆盖' };
const EFFECTIVE_KEYS = [...BOSS_FIELDS.map(([key]) => key), 'patterns'];

function hasOwn(object, key) {
  return Object.prototype.hasOwnProperty.call(object || {}, key);
}

function formatNumber(value) {
  return Number.isInteger(value) ? String(value) : String(Number(Number(value).toFixed(4)));
}

function resolveEffectiveBossLevel(builtInBossLevels, draft, selectedLevel) {
  const base = (builtInBossLevels || []).find((item) => Number(item.level) === Number(selectedLevel));
  if (!base) return null;
  const global = draft.bossGlobalOverrides || {};
  const level = (draft.bossLevelOverrides || {})[String(selectedLevel)] || {};
  const values = JSON.parse(JSON.stringify(base));
  const sources = Object.fromEntries(EFFECTIVE_KEYS.map((key) => [key, 'builtIn']));
  [['global', global], ['level', level]].forEach(([scope, overrides]) => {
    EFFECTIVE_KEYS.forEach((key) => {
      if (!hasOwn(overrides, key)) return;
      values[key] = Array.isArray(overrides[key]) ? [...overrides[key]] : overrides[key];
      sources[key] = scope;
    });
  });
  return { values, sources };
}

class BossConfigSection {
  constructor(configView) {
    this.view = configView;
    this.currentLevel = 1;
    this.manifestPromise = null;
    this.animalPreviewToken = 0;
  }

  get selectedLevel() {
    return this.currentLevel;
  }

  bind() {
    const fields = (id, scope) => {
      document.getElementById(id).innerHTML = BOSS_FIELDS.map(([key, label, min, max, step]) => `<label>${label}<input type="number" data-boss-scope="${scope}" data-boss-key="${key}" min="${min}" max="${max}" step="${step}" placeholder="留空继承当前值"></label>`).join('');
    };
    const patterns = (id, scope) => {
      document.getElementById(id).innerHTML = BOSS_PATTERNS.map(([key, label]) => `<label><input type="checkbox" data-boss-scope="${scope}" data-boss-pattern="${key}">${label}</label>`).join('');
    };
    fields('boss-global-fields', 'global'); fields('boss-level-fields', 'level');
    patterns('boss-global-patterns', 'global'); patterns('boss-level-patterns', 'level');
    document.getElementById('boss-level-select').innerHTML = Array.from({ length: 99 }, (_, index) => `<option value="${index + 1}">第 ${index + 1} 关</option>`).join('');
    const refresh = () => { this.syncDraft(); this.renderEffective(); };
    document.querySelectorAll('[data-boss-key]').forEach((input) => input.addEventListener('input', refresh));
    document.querySelectorAll('[data-boss-pattern]').forEach((input) => input.addEventListener('change', refresh));
    document.getElementById('boss-level-select').addEventListener('change', (event) => {
      this.syncDraft(); this.currentLevel = Number(event.target.value); this.renderInputs(); this.renderEffective();
    });
    document.getElementById('clear-boss-level').addEventListener('click', () => this.clearLevel());
  }

  readScope(scope) {
    const result = {};
    document.querySelectorAll(`[data-boss-scope="${scope}"][data-boss-key]`).forEach((input) => {
      if (input.value !== '') result[input.dataset.bossKey] = Number(input.value);
    });
    const checked = Array.from(document.querySelectorAll(`[data-boss-scope="${scope}"][data-boss-pattern]:checked`)).map((input) => input.dataset.bossPattern);
    if (checked.length) result.patterns = checked;
    return result;
  }

  syncDraft() {
    this.view.ensureBossDraft();
    this.view.draft.bossGlobalOverrides = this.readScope('global');
    const level = this.readScope('level');
    if (Object.keys(level).length) this.view.draft.bossLevelOverrides[String(this.selectedLevel)] = level;
    else delete this.view.draft.bossLevelOverrides[String(this.selectedLevel)];
  }

  renderInputs() {
    this.view.ensureBossDraft();
    const global = this.view.draft.bossGlobalOverrides;
    const level = this.view.draft.bossLevelOverrides[String(this.selectedLevel)] || {};
    document.getElementById('boss-level-select').value = String(this.selectedLevel);
    document.querySelectorAll('[data-boss-key]').forEach((input) => {
      const source = input.dataset.bossScope === 'global' ? global : level;
      input.value = source[input.dataset.bossKey] ?? '';
      input.dataset.path = `${input.dataset.bossScope === 'global' ? 'bossGlobalOverrides' : `bossLevelOverrides.${this.selectedLevel}`}.${input.dataset.bossKey}`;
    });
    document.querySelectorAll('[data-boss-pattern]').forEach((input) => {
      const source = input.dataset.bossScope === 'global' ? global : level;
      input.checked = Array.isArray(source.patterns) && source.patterns.includes(input.dataset.bossPattern);
      input.dataset.path = `${input.dataset.bossScope === 'global' ? 'bossGlobalOverrides' : `bossLevelOverrides.${this.selectedLevel}`}.patterns`;
    });
  }

  renderEffective() {
    const effective = resolveEffectiveBossLevel(this.view.builtInBossLevels, this.view.draft, this.selectedLevel);
    document.getElementById('boss-effective-title').textContent = `第 ${this.selectedLevel} 关 Boss 当前参数`;
    const grid = document.getElementById('boss-effective-grid');
    if (!effective) { grid.innerHTML = '<p class="hint">正在加载 Boss 参数…</p>'; return; }
    this.renderAnimalPreview(effective.values);
    const items = BOSS_FIELDS.map(([key, label]) => ({ key, label, value: formatNumber(effective.values[key]) }));
    items.push({ key: 'patterns', label: 'Boss 技能', value: effective.values.patterns.map((key) => PATTERN_LABELS[key] || key).join('、') || '—' });
    items.push({ key: 'variant', label: '造型', value: `配色 ${effective.values.variant.paletteIndex + 1} · 盔甲 ${effective.values.variant.armorIndex + 1}`, source: 'builtIn' });
    grid.innerHTML = items.map((item) => `<div class="effective-item"><span>${escapeHtml(item.label)}</span><strong>${escapeHtml(item.value)}</strong><small class="effective-source">${escapeHtml(SOURCE_LABELS[item.source || effective.sources[item.key]])}</small></div>`).join('');
  }

  renderAnimalPreview(values) {
    const image = document.getElementById('boss-animal-image');
    const name = document.getElementById('boss-animal-name');
    const animalName = values.animalName || '未知动物';
    const token = ++this.animalPreviewToken;
    name.textContent = `${animalName} · 第 ${this.selectedLevel} 关`;
    image.alt = `${animalName} Boss`;
    image.style.visibility = 'hidden';
    image.removeAttribute('src');
    if (!this.manifestPromise) {
      this.manifestPromise = fetch('/assets/boss/manifest.json', { cache: 'no-store' })
        .then((response) => {
          if (!response.ok) throw new Error('Boss manifest unavailable');
          return response.json();
        })
        .catch(() => null);
    }
    this.manifestPromise.then((manifest) => {
      if (token !== this.animalPreviewToken) return;
      const url = manifest && manifest.animals && manifest.animals[String(this.selectedLevel)];
      if (!url) return;
      image.onload = () => { if (token === this.animalPreviewToken) image.style.visibility = 'visible'; };
      image.onerror = () => { if (token === this.animalPreviewToken) image.style.visibility = 'hidden'; };
      image.src = url;
    });
  }

  clearLevel() {
    this.view.ensureBossDraft();
    delete this.view.draft.bossLevelOverrides[String(this.selectedLevel)];
    this.renderInputs(); this.renderEffective();
  }

  render() {
    this.renderInputs();
    this.renderEffective();
  }
}

BossConfigSection.resolveEffectiveBossLevel = resolveEffectiveBossLevel;
module.exports = BossConfigSection;
